import React, { CSSProperties, FunctionComponent, ReactNode, useLayoutEffect, useRef } from 'react';
import cn from '../cn';

type TextFieldUIProps = {
  label?: ReactNode,
  value?: string | number,
  defaultValue?: string | number,
  onChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void,
  variant?: 'standard' | 'outlined',
  size?: 'small' | 'medium',
  type?: string,
  placeholder?: string,
  fullWidth?: boolean,
  multiline?: boolean,
  minRows?: number,
  maxRows?: number,
  disabled?: boolean,
  required?: boolean,
  error?: boolean,
  helperText?: ReactNode,
  id?: string,
  name?: string,
  autoFocus?: boolean,
  className?: string,
  style?: CSSProperties,
  onKeyDown?: (e: React.KeyboardEvent) => void,
  onFocus?: (e: React.FocusEvent) => void,
  onBlur?: (e: React.FocusEvent) => void,
  inputRef?: React.Ref<HTMLInputElement>,
};

/**
 * Text field.
 *
 * Now renders the design system's `.ds-field` (bordered box, label above) — the
 * same control as `Input` and the component gallery. It previously drew its own
 * MUI border on an inline-styled overlay span with a floating label, which is
 * why `Autocomplete` looked out of place: its `renderInput` renders this
 * component, so every Autocomplete in the app inherited the old look.
 *
 * `variant` and `size` are kept in the prop type so the ~25 call sites still
 * compile, but they no longer change the appearance — the design system has one
 * field treatment, and having two would reintroduce exactly the drift this pass
 * is removing.
 */
const TextFieldUI: FunctionComponent<TextFieldUIProps> = ({
  label,
  value,
  defaultValue,
  onChange,
  type = 'text',
  placeholder,
  fullWidth = false,
  multiline = false,
  minRows,
  maxRows,
  disabled = false,
  required = false,
  error = false,
  helperText,
  id,
  name,
  autoFocus = false,
  className = '',
  style,
  onKeyDown,
  onFocus,
  onBlur,
  inputRef,
}) => {
  const taRef = useRef<HTMLTextAreaElement>(null);

  useLayoutEffect(() => {
    const el = taRef.current;
    if (!el || !multiline) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value, multiline]);

  const commonProps = {
    id,
    name,
    value,
    defaultValue,
    disabled,
    required,
    autoFocus,
    placeholder,
    onChange,
    onKeyDown,
    onFocus,
    onBlur,
    className: 'ds-field',
    'aria-invalid': error || undefined,
    'data-ds-invalid': error ? 'true' : undefined,
  };

  const textareaMax: CSSProperties = maxRows && multiline
    ? { maxHeight: `${maxRows * 1.4375}em`, overflow: 'auto' }
    : {};

  return (
    <div
      className={cn('ds-control-root inline-flex flex-col align-top', fullWidth && 'w-full', className)}
      style={style}
    >
      {label && (
        <label className="ds-label" htmlFor={id}>
          {label}
          {required && <span className="ds-required" aria-hidden="true">*</span>}
        </label>
      )}

      {multiline
        ? <textarea {...commonProps} ref={taRef} rows={minRows ?? 3} style={textareaMax} />
        : <input {...commonProps} ref={inputRef} type={type} />}

      {helperText !== undefined && (
        <span className="ds-hint" data-ds-invalid={error ? 'true' : undefined}>{helperText}</span>
      )}
    </div>
  );
};

export default TextFieldUI;
